// Segment Event Tracking
// Fire Segment track method for specific events
// https://segment.com/docs/sources/website/analytics.js/#track

import { tap } from "rxjs";

import store from "redux/store";
import { browserReportProperties } from "util/browser_report";

const WEB_CLIENT_SUFFIX = " [Web]";

// flattens object/array
// 'some string' => 'some string'
// { one: 1, two: { three: 3 }, four: [ 4, { five: 5 } ] } => { one: 1, 'two.three': 3, 'four.0': 4, 'four.1.five': 5 }
function flatten(value) {
  if (value !== Object(value)) {
    return value;
  }
  const flatObj = {};
  function _flatten(_value, prefix) {
    if (_value === Object(_value)) {
      Object.keys(_value).forEach((key) =>
        _flatten(_value[key], prefix ? `${prefix}.${key}` : key),
      );
    } else {
      flatObj[prefix] = _value;
    }
  }
  _flatten(value);
  return flatObj;
}

export function segmentTrack(event, properties = {}, options = {}, callback) {
  if (!window.analytics) {
    // If segment isn't there, we still need to resolve for downstream listeners.
    callback?.();
    return Promise.resolve();
  }
  const analyticsContext = getAnalyticsContext();
  const eventName = event + WEB_CLIENT_SUFFIX;
  const eventProps = {
    ...flatten(properties),
    ...browserReportProperties(),
    ...analyticsContext,
  };

  options = forceNoTrackIp(options);

  return window.analytics.track(eventName, eventProps, options, callback);
}

export function segmentTrackPromise(event, properties = {}, options = {}) {
  options = forceNoTrackIp(options);
  return new Promise((resolve) => segmentTrack(event, properties, options, resolve));
}

export function segmentIdentify(uid, traits = {}, options = {}, callback) {
  if (!window.analytics) {
    // If segment isn't there, we still need to resolve for downstream listeners.
    callback?.();
    return Promise.resolve();
  }
  const analyticsContext = getAnalyticsContext();

  options = forceNoTrackIp(options);
  return window.analytics.identify(
    uid,
    {
      ...traits,
      ...browserReportProperties(),
      ...analyticsContext,
    },
    options,
    callback,
  );
}

export function segmentReset() {
  return window.analytics && window.analytics.reset();
}

function getAnalyticsContext() {
  return store.getState().analytics || {};
}

function forceNoTrackIp(options) {
  return {
    ...options,
    context: {
      ...(options && options.context),
      ip: "0.0.0.0",
    },
  };
}

export function segmentTrackObservable(eventName, propertiesFn) {
  return (source) => source.pipe(tap((value) => segmentTrack(eventName, propertiesFn(value))));
}
